/* eslint-disable consistent-return */
import React, { useState, lazy } from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import differenceInDays from 'date-fns/difference_in_days';
import { Button } from 'reactstrap';

import { convertToGiftAtokCheck } from 'Redux/ducks/gift';
import ConfirmAccount from 'Components/Account/ConfirmAccount';
import DownloadAllBtn from 'Components/Order/DownloadAllBtn';
import Feedback from 'Components/Order/Feedback/Feedback';
import LazyModal from 'Components/Utility/LazyModal';
import ManageGift from 'Components/Order/ManageGift';
import MysteryBuyAgain from 'Components/Order/MysteryBuyAgain/MysteryBuyAgain';
import MysteryReveal from 'Components/Order/KeyReveals/MysteryReveal';
import OrderItem from 'Components/Order/OrderItem';
import SpecialFormattedNumber from 'Containers/Utility/Price/SpecialFormattedNumber';
import SupportFAQ from 'Components/Account/Support/SupportFAQ';
import SupportQuestions from 'Components/Account/Support/SupportQuestions';
import drmSupportLink from 'Helpers/Order/SupportLink';
import OperaGxCard from 'Components/Order/OperaGxCard';
import useViewport from 'Hooks/useViewport';
import imgixConfig from 'Config/imgix';

import 'Components/Order/Order.scss';

const OrderRefundModal = lazy(() => import(/* webpackChunkName: "OrderRefundModal" */ 'Components/Order/OrderRefundModal'));

function Order({
  order,
  emailConfirmed,
  setOrder,
  intl,
}) {
  const [currentOpenProductDetails, setCurrentOpenProductDetails] = useState(null);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [orderTab, setOrderTab] = useState('items');

  const { isAboveSm } = useViewport();

  const { formatMessage } = intl;

  const orderStatus = order.status;
  const orderAgeInDays = differenceInDays(new Date(), order.date);

  // Filter out the redeem code items to show them at the bottom of the order
  const filteredOrderItems = (order.items || []).filter(
    item => !item.drm.redeem,
  );

  const redeemOrderItems = (order.items || []).filter(
    item => item.drm.redeem,
  );

  const items = [...sortBy(filteredOrderItems, ['type']).reverse(), ...redeemOrderItems];

  const giftOrderRedeemDate = get(order, 'gift_order.redeem_date', order.date);

  const hasDownloads = order.items.some(
    item => (item.downloads && item.downloads.length) ||
      (item.bundles && item.bundles.length && item.bundles.some(
        bundle => bundle.games.some(
          game => game.downloads && game.downloads.length,
        ),
      )),
  );

  // Check the bundle items for mysteryRevealed.
  // If any are true the bundle has been seen and we don't want to show the reveal component
  const showMysteryReveal = (item) => {
    let showReveal = false;
    if (
      emailConfirmed &&
      orderStatus !== 'MANUAL_AUTHORIZATION' &&
      (item.reveal_template === 'pinata' || item.reveal_template === 'easter egg' || item.reveal_template === 'mystery bundle')
    ) {
      item.bundles.forEach((tier) => {
        if (tier.games) {
          tier.games.forEach((game) => {
            if (!game.mysteryRevealed) showReveal = true;
          });
        }
      });
    }
    return showReveal;
  };

  const openRefundModal = () => {
    setRefundModalOpen(true);
  };

  const orderDrms = [];
  const allIds = [];
  const allBundleIds = [];
  const allBundles = [];
  const mysteryBundleIds = [];
  const mysteryBundles = [];

  items.forEach((item) => {
    allIds.push(item._id);
    if (item.type === 'bundle' && !allBundleIds.includes(item._id)) {
      allBundleIds.push(item._id);
      allBundles.push(item);
    }
    if (item.mystery && item.type === 'bundle' && !mysteryBundleIds.includes(item._id)) {
      mysteryBundleIds.push(item._id);
      mysteryBundles.push(item);
    }
    const itemDrm = Object.keys(item.drm).filter(p => item.drm[p]);
    if (orderDrms.includes(itemDrm[0])) return false;
    orderDrms.push(itemDrm[0]);
  });

  const pnmItems = items.filter(i => i.pickAndMix);
  const nonPnmItems = items.filter(i => !i.pickAndMix);

  const pnmGroups = groupBy(pnmItems, 'pickAndMix');
  const pnmGroupsArray = Object.keys(pnmGroups).map(name => ({ name, items: pnmGroups[name] }));

  const showOperaGxCard = allIds.includes('66f1807864545595a91e2c9c') || allIds.includes('66f1897c8d142bdf23d8dcd2') || allIds.includes('673217bfce263d2718a42ac3');
  const hasLiquidCoolBundle = allIds.includes('66d078f2903fa012a5521748');

  // PnM and NonPnm arrays both need this functionality, so I split into separate
  // function for re-use. Bundles within PnM are treated a bit differently:
  // No bundle header, as it's already under a PnM header
  const orderItem = item =>
    <React.Fragment key={item.iid}>
      {!item.bundles.length ?
        <OrderItem
          game={item}
          orderID={order._id}
          bundleItem={item}
          emailConfirmed={emailConfirmed}
          order={order}
          currentOpenProductDetails={currentOpenProductDetails}
          setCurrentOpenProductDetails={setCurrentOpenProductDetails}
          setOrder={setOrder}
        />
        :
        <React.Fragment>
          {/* eslint-disable react/no-array-index-key */}

          {showMysteryReveal(item) ?
            <MysteryReveal
              orderID={order._id}
              item={item}
              setOrder={setOrder}
            />
            :
            <React.Fragment>
              {item.bundles.map((tier, i) =>
                <section className={item.pickAndMix ? 'bundle-section_in-pnm' : 'bundle-section'} key={`${item.iid}-tier${i}`}>
                  {!item.mystery && !item.pickAndMix &&
                    <div className="bundle-name-container">
                      <h3 className="bundle-name">
                        {item.isFreeProduct &&
                          <FormattedMessage id="order.isFreeProduct" defaultMessage="BONUS:" />
                        }
                        {item.name}
                      </h3>
                      {item.bundles.length > 1 && tier.games.length > 1 &&
                        <div className="bundle-tier">
                          <FormattedMessage id="order.tier" defaultMessage="Tier " />{i + 1}
                        </div>
                      }
                    </div>
                  }
                  {item.mystery && i === 0 &&
                    <div className="bundle-name-container">
                      <h3 className="bundle-name">
                        {item.name}
                      </h3>
                    </div>
                  }
                  <div className="order-bundle-items-container">
                    {sortBy(tier.games, ['iid']).map(game =>
                      <OrderItem
                        key={item.iid + game.iid}
                        game={game}
                        orderID={order._id}
                        bundleID={item._id}
                        bundleItem={item}
                        emailConfirmed={emailConfirmed}
                        order={order}
                        currentOpenProductDetails={currentOpenProductDetails}
                        setCurrentOpenProductDetails={setCurrentOpenProductDetails}
                      />,
                    )}
                  </div>
                </section>,
              )}
            </React.Fragment>
          }
        </React.Fragment>
      }
    </React.Fragment>;

  /* eslint-disable arrow-parens */
  /* eslint-disable react/style-prop-object */
  return (
    <section className="account-content single-order">
      <div className="title-button-container">
        <div className="title-container">
          <h1>
            {order.payment && order.payment.status === 'REDEEM_CODE' &&
              <React.Fragment>
                <FormattedMessage id="order.redeem" defaultMessage="Redeem" />&nbsp;
              </React.Fragment>
            }
            <FormattedMessage id="order.order-details" defaultMessage="Order Details" />
          </h1>
        </div>

        <div className="button-container">
          {orderTab === 'items' &&
            <button className="order-help-button" onClick={() => { setOrderTab('support'); }}>
              <FormattedMessage id="order.button.help-me" defaultMessage="Need help with your order?" />
            </button>
          }
          {orderTab === 'support' &&
            <button className="order-help-button" onClick={() => { setOrderTab('items'); }}>
              <FormattedMessage id="order.button.back-to-order" defaultMessage="Back to order" />
            </button>
          }

          {order.canConvertToGiftOrder &&
            <Button
              id="gift-button-trigger"
              className="btn-line-account"
              onClick={() => convertToGiftAtokCheck(order._id)}
            >
              <FormattedMessage id="order.giftthisorder" defaultMessage="Gift This Order" />
            </Button>
          }
        </div>
      </div>
      {!emailConfirmed && (!order.payment || order.payment.status !== 'REDEEM_CODE') &&
        <ConfirmAccount />
      }

      <div className="order-table-container">
        <div className="order-table-column order-number">
          <div className="column-title">
            <FormattedMessage id="order.order-number" defaultMessage="Order number" />
          </div>
          <div className="column-cell">
            {order._id}
          </div>
        </div>
        <div className="order-table-column order-date">
          <div className="column-title">
            {order.isGiftRecipient ?
              <FormattedMessage id="order.redeemeddate" defaultMessage="Redeemed Date" />
              :
              <FormattedMessage id="order.date" defaultMessage="Date" />
            }
          </div>
          <div className="column-cell">
            {order.date && !order.isGiftRecipient &&
              <FormattedDate value={order.date} year="numeric" month="short" day="2-digit" />
            }
            {order.date && order.isGiftRecipient &&
              <FormattedDate value={giftOrderRedeemDate} year="numeric" month="long" day="2-digit" />
            }
          </div>
        </div>
        <div className="order-table-column order-status">
          <div className="column-title">
            <FormattedMessage id="order.status" defaultMessage="Status" />
          </div>
          <div className="column-cell">
            {order.status}
          </div>
        </div>

        <div className="order-table-column order-type">
          <div className="column-title">
            {order.payment && order.payment.status !== 'REDEEM_CODE' && !order.isGiftRecipient &&
              <FormattedMessage id="order.total" defaultMessage="Total" />
            }
            {((order.payment && order.payment.status === 'REDEEM_CODE') || (order.isGiftRecipient)) &&
              <FormattedMessage id="order.type" defaultMessage="Type" />
            }
          </div>
          <div className="column-cell">
            {order.payment && order.payment.status !== 'REDEEM_CODE' && !order.isGiftRecipient &&
              <SpecialFormattedNumber
                dontConvert
                value={order.payment.total / 100}
                style="currency"
                currency={order.loc.currency_alphabetic_code}
              />
            }
            {(order.payment && order.payment.status === 'REDEEM_CODE') &&
              <FormattedMessage id="order.redeem-code" defaultMessage="Redeem Code" />
            }
            {order.isGiftRecipient &&
              <FormattedMessage id="order.gift" defaultMessage="Gift" />
            }
          </div>
        </div>
      </div>

      {order.isGiftSender &&
        <ManageGift
          gift={order.gift_order}
          items={order.items}
          orderId={order._id}
          isCompleteOrder={order.status === 'COMPLETE'}
          displayName={order.wishlist_display_name}
        />
      }

      {!order.isGiftSender &&
        <React.Fragment>
          {order.gift_order && order.gift_order.personal_message &&
            <section className="manage-gift pt-0 mb-0">
              <div className="gift-message-container">
                <h2 className="h3 mb-3">
                  <FormattedMessage id="order.gift.gift-message" defaultMessage="Gift Message" />
                </h2>
                <div className="gift-message-box">
                  {order.gift_order.personal_message}

                  {order.gift_order.gift_from &&
                    <div className="gift-message-box-from">
                      <FormattedMessage id="order.gift.gift-message-from" defaultMessage="From" />&nbsp;
                      {order.gift_order.gift_from}
                    </div>
                  }
                </div>
              </div>
            </section>
          }
          {orderTab === 'items' &&
            <section>
              <div className="title-download-button-container">
                <div>
                  <h2 className="h3 mb-0">
                    <FormattedMessage id="order.order-items" defaultMessage="Order Items" />
                  </h2>
                </div>
                {order.status === 'COMPLETE' && emailConfirmed && hasDownloads &&
                  <div className="download-button-container">
                    <DownloadAllBtn order={order} />
                  </div>
                }
              </div>

              {showOperaGxCard &&
                <section className="bundle-section bundle-section--success">
                  <div className="bundle-name-container">
                    <h3 className="bundle-name">
                      <FormattedMessage id="order.isFreeProduct.operagx" defaultMessage="BONUS: Opera GX Browser" />
                    </h3>
                  </div>
                  <div className="order-bundle-items-container">
                    <OperaGxCard />
                  </div>
                </section>
              }

              {hasLiquidCoolBundle &&
                <section className="bundle-section bundle-section--success">
                  <div className="bundle-name-container">
                    <h3 className="bundle-name">
                      <FormattedMessage id="order.isFreeProduct.liquid-cool" defaultMessage="BONUS: Austin Dragon Coupon" />
                    </h3>
                  </div>
                  <div className="order-bundle-items-container">
                    <article className="OrderItem--coupon">
                      {isAboveSm &&
                        <div className="OrderItem__cover">
                          <div className="responsive-image-island">
                            <div className="responsive-image responsive-image--16by9">
                              <img src={`https://fanatical.imgix.net/production/product/original/b9a8bc49-7fa6-4840-b901-62ad669e8283.png?${imgixConfig}`} alt="Opera GX: the browser for gamers" />
                            </div>
                          </div>
                        </div>
                      }
                      <div className="OrderItem__details">
                        <div className="OrderItem__details__text">
                          <FormattedMessage
                            id="order.isFreeProduct.liquid-cool.message"
                            defaultMessage="Discover more books from Austin Dragon and grab an extra {discount}."
                            values={{
                              discount: <FormattedMessage id="order.isFreeProduct.liquid-cool.message.discount" defaultMessage="50% OFF" tagName="span" />,
                            }}
                          />
                        </div>
                        <div className="OrderItem__details__buttonCode">
                          <div className="OrderItem__details__code">
                            Liquidcool50
                          </div>
                          <a href="https://www.austindragon.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-block">
                            <FormattedMessage id="order.isFreeProduct.liquid-cool.button" defaultMessage="Browse Austin Dragon" />
                          </a>
                        </div>
                      </div>
                    </article>
                  </div>
                </section>
              }

              {pnmGroupsArray.map(pnm =>
                <section className="bundle-section" key={pnm.name}>
                  <div className="bundle-name-container">
                    <h3 className="bundle-name">{pnm.name}</h3>
                  </div>
                  <div className="order-bundle-items-container">
                    {sortBy(pnm.items, ['name']).map(orderItem)}
                  </div>
                </section>,
              )}
              {nonPnmItems.map(orderItem)}

              {!isEmpty(mysteryBundles) && orderAgeInDays < 30 && mysteryBundles.map((bundle) =>
                <MysteryBuyAgain key={bundle._id} item={bundle} />,
              )}

              {!isEmpty(allBundles) && allBundles.map((bundle) => {
                if (bundle._id === '65cf4b5480ef96dd1a7d5123') return false;
                return (
                  <Feedback
                    key={bundle._id}
                    productName={bundle.name}
                    productId={bundle._id}
                    orderId={order._id}
                    slug={bundle.slug}
                  />
                );
              })}
            </section>
          }
        </React.Fragment>
      }
      {orderTab === 'support' &&
        <section>
          <div className="title-download-button-container">
            <h2 className="h3 mb-0">
              <FormattedMessage id="order.header.support" defaultMessage="Order Support" />
            </h2>
          </div>
          <p>
            <FormattedMessage
              id="order.support.faqs"
              defaultMessage="We're here to help! Check out our FAQs or click the button to go through to our full support centre."
            />
          </p>

          <div className="account-table support-faqs">
            <div className="account-table-heading includes-button">
              <div className="title-container">
                <FormattedMessage id="order.support.subheader" defaultMessage="Frequently Asked Questions" />
              </div>
              <div className="button-container">
                <a className="btn btn-line-account">
                  <FormattedMessage id="order.support.button" defaultMessage="Support Center" />
                </a>
              </div>
            </div>

            {orderDrms.map((drm) => {
              if (drm === 'steam' || drm === 'uplay' || drm === 'rockstar' || drm === 'betheda' || drm === 'esonline' || drm === 'epicgames' || drm === 'origin' || drm === 'oculus' || drm === 'gog' || drm === 'magix' || drm === 'zenva' || drm === 'utalk') {
                return (
                  <SupportFAQ
                    key={drm}
                    question={formatMessage(SupportQuestions[`how_to_use_${drm}_key`])}
                    supportArticle={drmSupportLink(drm)}
                  />
                );
              }
              return false;
            })}

            <SupportFAQ
              question={formatMessage(SupportQuestions.how_to_use_all_other_key)}
              supportArticle="115005516509-How-do-I-use-my-new-keys-"
            />

            <SupportFAQ
              question={formatMessage(SupportQuestions.key_time_limit)}
              supportArticle="207289349-Do-I-have-a-time-limit-for-redeeming-a-key-"
            />

            <SupportFAQ
              question={formatMessage(SupportQuestions.why_manual_auth)}
              supportArticle="201027912-Order-Status-Manual-Authorisation-Why-do-I-have-them-"
            />

            <SupportFAQ
              question={formatMessage(SupportQuestions.activate_gift_card)}
              supportArticle="10821046183057-How-do-I-activate-my-gift-card-"
            />

            {!order.canBeRefunded &&
              <SupportFAQ
                question={formatMessage(SupportQuestions.can_i_refund)}
                supportArticle="201027572-Can-I-get-a-refund-cancel-my-order-"
              />
            }

            {order.canBeRefunded &&
              <div className="account-table-row-link">
                <div className="account-table-row">
                  <div className="account-table-row-content">
                    <div className="account-table-details-container">
                      <FormattedMessage id="order.refund.question" defaultMessage="Can I get a refund/cancel my order?" />
                    </div>
                    <div className="account-table-action-container">
                      <button className="btn account-table-action-button" onClick={openRefundModal}>
                        <FormattedMessage id="order.refund.button" defaultMessage="Request Refund" />
                      </button>
                      <LazyModal
                        open={refundModalOpen}
                        setOpen={setRefundModalOpen}
                        size="lg"
                        className="ModalDefault"
                      >
                        <OrderRefundModal order={order} closeModal={setRefundModalOpen} />
                      </LazyModal>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      }
    </section>
  );
}

export default injectIntl(Order);

Order.propTypes = {
  order: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  emailConfirmed: PropTypes.bool.isRequired,
  setOrder: PropTypes.func,
};

Order.defaultProps = {
  order: {
    items: [],
  },
  setOrder: () => {},
};
